import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Text, Content, Col, Row, Grid, FlexboxGrid } from 'rsuite';
import {
  setFetchedRegistrantDetails,
  updatePermitData,
} from '../../store/slices/adminUpdate/adminUpdateSlice';
import FlowPageLayout from '../../components/layout/FlowPageLayout';
import PortalHeader from '../../components/layout/PortalHeader';
import SchemaTyped from 'rsuite/esm/Schema/Schema';
import {
  companyNameValidation,
  nameRequiredValidation,
  phoneRequiredValidation,
  requiredValidation,
} from '../../common/validation';
import PersonDetails from '../../components/form/PersonDetails';
import {
  ADMIN_UPDATE_ACCOUNT_OPTIONS_ENUM,
  FLOWS_ENUM,
} from '../../common/enums';
import {
  selectAdminUpdatedPermitData,
  selectFetchedRegistrantDetails,
} from '../../store/slices/adminUpdate/adminUpdateSelectors';
import Notice from '../../components/user-feedback/Notice';
import { selectConfigExpressRegistrationTypeVerbiage } from '../../store/slices/config/configSelectors';
import pluralize from 'pluralize';
import { getUserDetailsFromEmailAddress } from '../../store/slices/adminUpdate/adminUpdateThunks';
import IconIndicator from '../../components/user-feedback/IconIndicator';

const AdminUpdateRegistrant = () => {
  // State and dependencies
  const dispatch = useDispatch();

  const registrantForm = useRef();
  const [registrantFormValues, setRegistrantFormValues] = useState({});
  const [registrantFormError, setRegistrantFormError] = useState({});
  const [isLoadingMoveToExisting, setIsLoadingMoveToExisting] = useState(false);
  const [isLoadingNumLinkedLicenses, setIsLoadingNumLinkedLicenses] =
    useState(false);
  const [warningText, setWarningText] = useState('');
  const [
    fetchExisitngUserDetailsSuccessful,
    setFetchExisitngUserDetailsSuccessful,
  ] = useState(false);

  // Selectors
  const selectedApplicationData = useSelector(selectAdminUpdatedPermitData);
  const licenseTypeVerbiage = useSelector(
    selectConfigExpressRegistrationTypeVerbiage
  );
  const fetchedRegistrantDetails = useSelector(selectFetchedRegistrantDetails);
  const {
    registrant,
    updateAccountOption,
    initialRegistrantData,
    updateAllPermits,
  } = useSelector(selectAdminUpdatedPermitData);

  const registrantFormModel = useMemo(() => {
    return {
      firstName: '',
      lastName: '',
      companyName: '',
      phone: '',
      mailingAddress: '',
      sameAsPropertyAddress: false,
    };
  }, []);

  const { BooleanType } = SchemaTyped.Types;

  const registrantValidationModel = {
    firstName: nameRequiredValidation,
    lastName: nameRequiredValidation,
    companyName: companyNameValidation,
    phone: phoneRequiredValidation,
    mailingAddress: requiredValidation,
    sameAsPropertyAddress: BooleanType(),
  };

  useEffect(() => {
    const fetchNewAccountOrUnchangedLinkedPermitsWarning = async () => {
      if (initialRegistrantData?.email) {
        setIsLoadingNumLinkedLicenses(true);
        const detailsOfAccountMovingTo = await dispatch(
          getUserDetailsFromEmailAddress(initialRegistrantData.email)
        );

        if (detailsOfAccountMovingTo.successful) {
          const numLicenses = detailsOfAccountMovingTo?.result?.licenseCount;
          setWarningText(
            <Text>
              Please note that this portal account is linked to{' '}
              <strong>{numLicenses}</strong> active{' '}
              {pluralize(licenseTypeVerbiage.toLowerCase(), numLicenses)}.
              Updating registrant details here will update them across all
              linked {pluralize(licenseTypeVerbiage.toLowerCase())}.
            </Text>
          );
        } else {
          setWarningText(
            <Text>
              Please note that this portal account may be linked to other active{' '}
              {pluralize(licenseTypeVerbiage.toLowerCase())}. Updating
              registrant details here will update them across all linked{' '}
              {pluralize(licenseTypeVerbiage.toLowerCase())}.
            </Text>
          );
        }
      }
      setIsLoadingNumLinkedLicenses(false);
    };

    const fetchExistingAccountWarning = () => {
      const permitPart = updateAllPermits
        ? `all ${pluralize(licenseTypeVerbiage.toLowerCase())}`
        : `this ${pluralize(licenseTypeVerbiage.toLowerCase(), 1)}`;
      const secondPermitPart = updateAllPermits
        ? pluralize(licenseTypeVerbiage.toLowerCase())
        : pluralize(licenseTypeVerbiage.toLowerCase(), 1);

      setWarningText(
        <Grid fluid>
          <Row>
            <Col xs={24}>
              <Text className="paragraph">
                As you have elected to move {permitPart} to an existing portal
                account, the information displayed below belongs to the
                destination account.
              </Text>
              <Text className="paragraph">
                Updating any information here will propagate across{' '}
                <strong>all</strong>{' '}
                {pluralize(licenseTypeVerbiage.toLowerCase())} attached to this
                account, including any existing{' '}
                {pluralize(licenseTypeVerbiage.toLowerCase())} plus the{' '}
                {secondPermitPart} you are moving.
              </Text>
            </Col>
          </Row>
        </Grid>
      );
    };

    if (
      updateAccountOption ===
      ADMIN_UPDATE_ACCOUNT_OPTIONS_ENUM.MOVETOEXISTINGACCOUNT
    ) {
      fetchExistingAccountWarning();
    } else if (
      updateAccountOption === ADMIN_UPDATE_ACCOUNT_OPTIONS_ENUM.NEWACCOUNT ||
      updateAccountOption === ADMIN_UPDATE_ACCOUNT_OPTIONS_ENUM.NOCHANGE
    ) {
      fetchNewAccountOrUnchangedLinkedPermitsWarning();
    }
  }, [
    initialRegistrantData,
    dispatch,
    updateAccountOption,
    licenseTypeVerbiage,
    updateAllPermits,
  ]);

  useEffect(() => {
    const setFormValues = (details = {}) => {
      setRegistrantFormValues({
        firstName: details.firstName ?? '',
        lastName: details.lastName ?? '',
        companyName: details.companyName ?? '',
        phone: details.phone ?? '',
        mailingAddress: details.mailingAddress ?? '',
        sameAsPropertyAddress:
          details.mailingAddress &&
          details.mailingAddress.toLowerCase() ===
            selectedApplicationData?.property?.propertyaddress.toLowerCase(),
      });
    };

    const populateRegistrantForm = async () => {
      const registrantDetails = registrant.registrant || {};

      if (
        updateAccountOption ===
        ADMIN_UPDATE_ACCOUNT_OPTIONS_ENUM.MOVETOEXISTINGACCOUNT
      ) {
        await populateDetailsMoveToExistingAccount(
          registrantDetails.email,
          registrantDetails
        );
      } else {
        setFormValues(registrantDetails);
      }
    };

    const populateDetailsMoveToExistingAccount = async (
      email,
      registrantDetails
    ) => {
      setIsLoadingMoveToExisting(true);

      if (fetchedRegistrantDetails) {
        setFetchExisitngUserDetailsSuccessful(true);
        setFormValues(registrantDetails);
      } else {
        const detailsOfAccountMovingTo = await dispatch(
          getUserDetailsFromEmailAddress(email)
        );
        const isSuccessful = detailsOfAccountMovingTo.successful;
        setFetchExisitngUserDetailsSuccessful(isSuccessful);

        if (isSuccessful) {
          setFormValues(detailsOfAccountMovingTo.result?.user);
        } else {
          setFormValues();
        }
      }

      setIsLoadingMoveToExisting(false);
    };

    populateRegistrantForm();
  }, [registrant, updateAccountOption, fetchedRegistrantDetails]);

  const dispatchRegistrant = () => {
    const isValid = registrantForm.current.check();

    if (isValid && canContinue) {
      const updatedRegistrantDetails = {
        firstName: registrantFormValues?.firstName,
        lastName: registrantFormValues?.lastName,
        phone: registrantFormValues?.phone,
        mailingAddress: registrantFormValues?.mailingAddress,
        companyName: registrantFormValues?.companyName,
      };

      dispatch(
        updatePermitData({
          registrant: {
            ...registrant,
            registrant: {
              ...registrant.registrant,
              ...updatedRegistrantDetails,
            },
          },
        })
      );

      dispatch(setFetchedRegistrantDetails(true));
      return true;
    } else {
      return false;
    }
  };

  const onSameAsPropertyAddressChanged = (checked) => {
    setRegistrantFormValues((prevFormValues) => ({
      ...prevFormValues,
      sameAsPropertyAddress: checked,
      mailingAddress: checked
        ? selectedApplicationData?.property?.propertyaddress
        : prevFormValues.mailingAddress,
    }));
  };

  const canContinue = useMemo(() => {
    if (isLoadingNumLinkedLicenses || isLoadingMoveToExisting) {
      return false;
    }

    const isMoveToExistingAccount =
      updateAccountOption ===
      ADMIN_UPDATE_ACCOUNT_OPTIONS_ENUM.MOVETOEXISTINGACCOUNT;

    return (
      !isMoveToExistingAccount ||
      (isMoveToExistingAccount && fetchExisitngUserDetailsSuccessful)
    );
  }, [
    fetchExisitngUserDetailsSuccessful,
    updateAccountOption,
    isLoadingNumLinkedLicenses,
    isLoadingMoveToExisting,
  ]);

  const showNotice = useMemo(() => {
    const isNotMoving =
      updateAccountOption === ADMIN_UPDATE_ACCOUNT_OPTIONS_ENUM.NOCHANGE;
    const isMoveToExisting =
      updateAccountOption ===
      ADMIN_UPDATE_ACCOUNT_OPTIONS_ENUM.MOVETOEXISTINGACCOUNT;
    const isNewAccountWithAllPermitsToBeUpdated =
      updateAccountOption === ADMIN_UPDATE_ACCOUNT_OPTIONS_ENUM.NEWACCOUNT &&
      updateAllPermits;

    return (
      isNotMoving || isMoveToExisting || isNewAccountWithAllPermitsToBeUpdated
    );
  }, [updateAccountOption, updateAllPermits]);

  const showFailedToFetchExistingRegistrantDetailsMessage = useMemo(() => {
    return (
      updateAccountOption ===
        ADMIN_UPDATE_ACCOUNT_OPTIONS_ENUM.MOVETOEXISTINGACCOUNT &&
      !isLoadingMoveToExisting &&
      !fetchExisitngUserDetailsSuccessful
    );
  }, [isLoadingMoveToExisting, fetchExisitngUserDetailsSuccessful]);

  // when we navigate to the previous from this page, we need to set it to false, so the next to we go to this we re-fetch the regiatrant details
  const handleBackClick = () => {
    dispatch(setFetchedRegistrantDetails(false));
    return true;
  };

  return (
    <FlexboxGrid justify="space-between">
      <FlexboxGrid.Item colspan={24}>
        <PortalHeader
          flow={FLOWS_ENUM.ADMIN_UPDATE}
          showAccountDropdown={false}
        />
      </FlexboxGrid.Item>
      <FlexboxGrid.Item colspan={24}>
        <Content>
          <FlowPageLayout
            title="Registrant"
            onNext={dispatchRegistrant}
            onBack={handleBackClick}
            hideNextButton={!canContinue}
          >
            <Grid
              className="ml-0 "
              xs={24}
              lg={12}
              fluid
            >
              {showNotice && (
                <Row>
                  <Col xs={24}>
                    {!isLoadingNumLinkedLicenses && (
                      <Notice
                        className="mb-24"
                        type="warning"
                        message={warningText}
                        iconSize={16}
                        isMessageComponent={true}
                      />
                    )}
                    {isLoadingNumLinkedLicenses && <IconIndicator />}
                  </Col>
                </Row>
              )}

              <Row className="mt-24 mb-8">
                <Col xs={12}>
                  <Text className="text-bold">Registrant Details</Text>
                </Col>
              </Row>
              <Row className="mb-24">
                <Col
                  xs={24}
                  className="mb-4"
                >
                  <Text>Registrant Email / Portal Login</Text>
                </Col>
                <Col xs={24}>
                  <Text>{registrant.registrant.email}</Text>
                </Col>
              </Row>

              {isLoadingMoveToExisting && <IconIndicator />}
              {!isLoadingMoveToExisting &&
                !showFailedToFetchExistingRegistrantDetailsMessage && (
                  <Row>
                    <Col
                      xs={24}
                      lg={12}
                    >
                      <PersonDetails
                        ref={registrantForm}
                        showEmail={false}
                        setFormError={setRegistrantFormError}
                        setFormValues={(v) => {
                          setRegistrantFormValues(v);
                        }}
                        formModel={registrantFormModel}
                        formError={registrantFormError}
                        formValues={registrantFormValues}
                        validationModel={registrantValidationModel}
                        showSameAsPropertyAddressCheckboxOption={true}
                        onSameAsPropertyAddressChanged={
                          onSameAsPropertyAddressChanged
                        }
                      />
                    </Col>
                  </Row>
                )}
              {showFailedToFetchExistingRegistrantDetailsMessage && (
                <Row>
                  <Col xs={24}>
                    <Notice
                      message={
                        'Something went wrong while trying to fetch registrant details.'
                      }
                    />
                  </Col>
                </Row>
              )}
            </Grid>
          </FlowPageLayout>
        </Content>
      </FlexboxGrid.Item>
    </FlexboxGrid>
  );
};

export default AdminUpdateRegistrant;
